/**
 * 全局搜索的实体查询（方案 §3.2）：去掉首尾空格后 ≥ 2 个字符才请求；停止输入 200 ms 后发；新请求发出前取消上一个；
 * 同一个输入的结果在本次会话内缓存 60 秒。客人名 ≥ 3 个字符、纯数字按编号还是按 Snowflake 查，都由后端判断。
 */
import { useEffect, useState } from 'react'
import { entitiesApi, type EntityRef } from '@/api/entitiesApi'
import { ENTITY_SEARCH_REQUEST_TYPES, isEntitySearchType } from './searchTypes'

export const MIN_CHARS = 2
export const DEBOUNCE_MS = 200
const CACHE_TTL_MS = 60_000
/** 每组显示几条；多要一条，用来判断「还有更多」 */
export const SHOWN_PER_TYPE = 5
const LIMIT_PER_TYPE = SHOWN_PER_TYPE + 1

export type EntitySearchStatus = 'idle' | 'loading' | 'done' | 'error'

interface SearchState {
  status: EntitySearchStatus
  query: string
  refs: EntityRef[]
}

/** 同 main-api `entity_resolver/schemas.py` 的 MAX_IDENTIFIER_LENGTH */
export const MAX_QUERY_LENGTH = 200

/** Listings 组里最少留给虚拟地点的行数 */
const PLACE_QUOTA = 2

/**
 * 后端的 `place`（虚拟地点）和 `listing` 是两类，各回各的。界面上合成一组，完全命中排前面。
 * 这一组没有「在列表里看」，不留名额的话虚拟地点会被出租房挤掉，找不到。
 */
function mergePlaces(refs: EntityRef[]): EntityRef[] {
  const places = refs.filter((r) => r.entityType === 'place').map((r) => ({ ...r, entityType: 'listing' }))
  if (places.length === 0) return refs
  const listings = refs.filter((r) => r.entityType === 'listing')
  const isPlace = (r: EntityRef) => r.entityType === 'listing' || r.entityType === 'place'
  const kept = listings.slice(0, SHOWN_PER_TYPE - Math.min(places.length, PLACE_QUOTA))
  const merged = [...kept, ...places].sort((a, b) => Number(!!b.exact) - Number(!!a.exact))
  // 组的顺序由后端定：合并后的组留在这一组原来的位置
  const at = refs.findIndex(isPlace)
  return [...refs.slice(0, at), ...merged, ...refs.slice(at).filter((r) => !isPlace(r))]
}

const cache = new Map<string, { at: number; refs: EntityRef[] }>()

export function clearEntitySearchCache(): void {
  cache.clear()
}

// 跨用户隔离：退出 / 401 都清（同 viewsLookupCache）
if (typeof window !== 'undefined') {
  window.addEventListener('auth:unauthorized', clearEntitySearchCache)
}

/** `scope`：缓存按它分开（当前用户 id）。 */
export function useEntitySearch(input: string, scope: string): SearchState & { retry: () => void } {
  // 后端一个标识最长 200 字符，超了整个请求 422（面板只会显示「搜索不可用」）：粘进来的长文本只拿前 200 个查
  const query = input.trim().slice(0, MAX_QUERY_LENGTH).trim()
  const [state, setState] = useState<SearchState>({ status: 'idle', query: '', refs: [] })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (query.length < MIN_CHARS) {
      setState({ status: 'idle', query, refs: [] })
      return
    }
    const key = `${scope}\u0000${query}`
    const hit = cache.get(key)
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) {
      setState({ status: 'done', query, refs: hit.refs })
      return
    }
    setState({ status: 'loading', query, refs: [] })
    const controller = new AbortController()
    const timer = setTimeout(() => {
      entitiesApi
        .resolve({ identifiers: [query], types: [...ENTITY_SEARCH_REQUEST_TYPES], mode: 'search', limitPerType: LIMIT_PER_TYPE },
          controller.signal)
        .then((results) => {
          if (controller.signal.aborted) return
          const refs = mergePlaces(results[query] ?? Object.values(results)[0] ?? [])
            .filter((r) => isEntitySearchType(r.entityType))
          cache.set(key, { at: Date.now(), refs })
          setState({ status: 'done', query, refs })
        })
        .catch(() => {
          if (controller.signal.aborted) return
          setState({ status: 'error', query, refs: [] })
        })
    }, DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, scope, attempt])

  return { ...state, retry: () => setAttempt((n) => n + 1) }
}
